import { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';
import { useAdjudicatorMembership } from '../../hooks/useAdjudicatorMembership';
import Logo from '../Logo';

interface NavItem {
  path: string;
  labelKey: string;
  icon: string;
  activePrefix?: string; // if set, item is active for any path that starts with this prefix
}

// Items shown to every authenticated user.
const platformNav: NavItem[] = [
  { path: '/dashboard', labelKey: 'sidebar.platform.dashboard', icon: 'dashboard' },
  { path: '/trust-explorer', labelKey: 'sidebar.platform.trustExplorer', icon: 'verified_user' },
  { path: '/my-dids', labelKey: 'sidebar.platform.myDids', icon: 'badge' },
  { path: '/resolve', labelKey: 'sidebar.platform.didResolver', icon: 'travel_explore' },
  { path: '/ai-agents', labelKey: 'sidebar.platform.aiAgents', icon: 'smart_toy' },
  { path: '/upi', labelKey: 'sidebar.platform.upiPayments', icon: 'currency_rupee' },
  { path: '/zk-identity', labelKey: 'sidebar.platform.zkIdentity', icon: 'lock' },
  { path: '/settings', labelKey: 'sidebar.platform.settings', icon: 'settings' },
];

// Shown to holders only — credentials they received, not ones they issue/verify.
const holderNav: NavItem[] = [
  { path: '/my-credentials', labelKey: 'sidebar.platform.myCredentials', icon: 'workspace_premium' },
];

const networkNav: NavItem[] = [
  { path: '/schemas', labelKey: 'sidebar.network.schemas', icon: 'schema' },
  { path: '/agent-marketplace', labelKey: 'sidebar.network.agentMarketplace', icon: 'storefront' },
];

// Operator section — only relevant for users with operator role.
const operatorNav: NavItem[] = [
  { path: '/operators/me/listings', labelKey: 'sidebar.operator.myListings', icon: 'inventory_2' },
  { path: '/operators/me/escrows', labelKey: 'sidebar.operator.myEscrows', icon: 'lock_clock' },
  { path: '/operators/me/earnings', labelKey: 'sidebar.operator.earnings', icon: 'paid' },
];

// Onboarding is removed — an issuer is already onboarded by definition.
const issuerNav: NavItem[] = [
  { path: '/issuer/schemas', labelKey: 'sidebar.issuer.schemas', icon: 'schema' },
  { path: '/issuer/issue', labelKey: 'sidebar.issuer.issue', icon: 'verified' },
  { path: '/issuer/bulk-issue', labelKey: 'sidebar.issuer.bulkIssue', icon: 'upload_file' },
  { path: '/issuer/revocations', labelKey: 'sidebar.issuer.revocations', icon: 'block' },
  { path: '/issuer/analytics', labelKey: 'sidebar.issuer.analytics', icon: 'bar_chart' },
];

// Onboarding is removed — a verifier is already onboarded by definition.
const verifierNav: NavItem[] = [
  { path: '/verifier/proof-request', labelKey: 'sidebar.verifier.proofRequests', icon: 'qr_code_2' },
  { path: '/verifier/verify', labelKey: 'sidebar.verifier.verify', icon: 'fact_check' },
  { path: '/verifier/history', labelKey: 'sidebar.verifier.history', icon: 'history' },
  { path: '/verifier/trust-framework', labelKey: 'sidebar.verifier.trustFramework', icon: 'security' },
  { path: '/verifier/attestations/issue', labelKey: 'sidebar.verifier.issueAttestation', icon: 'rate_review' },
];

const adminNav: NavItem[] = [
  { path: '/admin/role-applications', labelKey: 'sidebar.admin.roleApplications', icon: 'manage_accounts' },
  { path: '/admin/users/issuer', labelKey: 'sidebar.admin.users', icon: 'group', activePrefix: '/admin/users' },
  { path: '/docs', labelKey: 'sidebar.admin.documentation', icon: 'menu_book' },
];

// Phase 7-F.2 — adjudicator entry. Visibility gated by
// useAdjudicatorMembership() at render time (not by user role) because
// adjudicators are wallet-address-keyed, not auth-role-keyed.
const adjudicatorNav: NavItem[] = [
  { path: '/adjudicator', labelKey: 'sidebar.adjudicator.dashboard', icon: 'gavel' },
];

function NavSection({ title, items }: { title: string; items: NavItem[] }) {
  const { t } = useTranslation('layout');
  const { pathname } = useLocation();
  return (
    <>
      {title && (
        <div className="mb-2 mt-8 px-6 first:mt-0">
          <span className="text-[10px] font-extrabold uppercase tracking-widest text-text-faint">
            {title}
          </span>
        </div>
      )}
      {items.map((item) => {
        const prefixActive = item.activePrefix ? pathname.startsWith(item.activePrefix) : false;
        return (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `sidebar-item ${isActive || prefixActive ? 'active' : ''}`
            }
          >
            <span className="material-symbols-outlined">{item.icon}</span>
            {t(item.labelKey)}
          </NavLink>
        );
      })}
    </>
  );
}

const roleBadgeColor: Record<string, string> = {
  admin: 'text-purple-600',
  issuer: 'text-blue-600',
  verifier: 'text-green-600',
  holder: 'text-slate-600',
};

export default function Sidebar() {
  const { user, hasRole, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['layout', 'common']);
  const [profileOpen, setProfileOpen] = useState(false);

  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  const isAdmin = user?.role === 'admin';
  const adjudicatorMembership = useAdjudicatorMembership();

  return (
    <aside className="flex w-64 flex-col border-r border-border-subtle bg-white">
      {/* Logo */}
      <NavLink
        to="/dashboard"
        aria-label={t('sidebar.appName')}
        className="flex h-16 items-center border-b border-border-subtle px-6"
      >
        <Logo className="h-7 w-auto text-text-main" />
      </NavLink>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-6">
        {/* Platform — shown to all roles */}
        <NavSection title={t('sidebar.sections.platform')} items={platformNav} />

        {/* Verifications — useful for issuers/verifiers/admins, not plain holders */}
        {user?.role !== 'holder' && (
          <NavLink to="/verifications" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
            <span className="material-symbols-outlined">fact_check</span>
            {t('sidebar.platform.verifications')}
          </NavLink>
        )}

        {/* Holder-specific: My Credentials + Apply for Role upgrade path */}
        {user?.role === 'holder' && (
          <>
            <NavSection title={t('sidebar.sections.myCredentials')} items={holderNav} />
            <NavLink to="/role-application" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
              <span className="material-symbols-outlined">upgrade</span>
              {t('sidebar.platform.applyForRole')}
            </NavLink>
          </>
        )}

        {/* Network — all roles except holder */}
        {user?.role !== 'holder' && (
          <NavSection title={t('sidebar.sections.network')} items={networkNav} />
        )}

        {/* Operator — only for admin (operator role not in current auth model) */}
        {isAdmin && (
          <NavSection title={t('sidebar.sections.operator')} items={operatorNav} />
        )}

        {/* Issuer Portal. No onboarding link for anyone, admins included:
            onboarding is how a holder applies, via Apply for Role. */}
        {hasRole('issuer') && (
          <NavSection
            title={t('sidebar.sections.issuerPortal')}
            // The super admin does not author schemas, so My Schemas is hidden;
            // they manage every issuer's schemas from Network > Schemas.
            items={isAdmin ? issuerNav.filter((i) => i.path !== '/issuer/schemas') : issuerNav}
          />
        )}

        {/* Verifier Portal — no onboarding link, as for the Issuer Portal. */}
        {hasRole('verifier') && (
          <NavSection title={t('sidebar.sections.verifierPortal')} items={verifierNav} />
        )}

        {adjudicatorMembership.isAdjudicator && (
          <NavSection title={t('sidebar.sections.adjudicator')} items={adjudicatorNav} />
        )}
        {isAdmin && <NavSection title={t('sidebar.sections.admin')} items={adminNav} />}

        {/* System Status */}
        <div className="mx-4 mt-8 rounded-2xl border border-border-subtle bg-slate-50 p-4">
          <div className="mb-2 flex items-center gap-2">
            <span className="size-2 animate-pulse rounded-full bg-emerald-500"></span>
            <span className="text-xs font-bold text-text-main">{t('common:system.operational')}</span>
          </div>
          <p className="text-[11px] leading-relaxed text-text-faint">
            {t('common:system.networkInfo')}
          </p>
        </div>
      </nav>

      {/* User Profile */}
      <div className="relative border-t border-border-subtle p-4">
        {profileOpen && (
          <div className="absolute bottom-full left-2 right-2 mb-1 rounded-xl border border-border-subtle bg-white shadow-lg">
            <button onClick={() => { navigator.clipboard.writeText(user?.email ?? ''); setProfileOpen(false); }}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-text-main hover:bg-slate-50 rounded-t-xl">
              <span className="material-symbols-outlined shrink-0 text-text-faint" style={{ fontSize: 18 }}>mail</span>
              {/* min-w-0 + truncate: a long address ellipsises instead of
                  wrapping oddly or widening the popover. Full value on hover. */}
              <span className="min-w-0 truncate" title={user?.email}>{user?.email ?? t('sidebar.profile.noEmail')}</span>
            </button>
            <button onClick={() => { navigate('/my-dids'); setProfileOpen(false); }}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-text-main hover:bg-slate-50">
              <span className="material-symbols-outlined text-text-faint" style={{ fontSize: 18 }}>badge</span>
              {t('sidebar.profile.myDids')}
            </button>
            <button onClick={() => { logout(); setProfileOpen(false); }}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-b-xl">
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>logout</span>
              {t('sidebar.profile.signOut')}
            </button>
          </div>
        )}
        <button onClick={() => setProfileOpen(!profileOpen)} className="flex w-full items-center gap-3">
          <div className="flex size-8 items-center justify-center rounded-full bg-indigo-100">
            <span className="text-xs font-bold text-indigo-700">{initials}</span>
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="truncate text-sm font-bold text-text-main">{user?.name ?? t('sidebar.profile.unknown')}</p>
            <p className={`text-[10px] font-bold capitalize ${roleBadgeColor[user?.role ?? 'holder']}`}>
              {user?.role === 'admin' ? t('sidebar.profile.superAdmin') : user?.role ?? t('sidebar.profile.noRole')}
            </p>
          </div>
          <span className="material-symbols-outlined text-text-faint transition-transform" style={{ fontSize: 18, transform: profileOpen ? 'rotate(180deg)' : '' }}>
            expand_more
          </span>
        </button>
      </div>
    </aside>
  );
}
